import { assessClaim, } from "./epistemic.js";
const MAX_DEPTH = 5;
function lineageNodes(refs, resolve, depth, seen) {
    return refs.map((ref) => {
        if (ref.kind !== "claim") {
            return {
                ref,
                proposition: null,
                claimType: null,
                origin: null,
                inferenceMode: null,
                condition: null,
                superseded: false,
                missing: false,
                inputs: [],
            };
        }
        const input = resolve(ref.id);
        if (!input) {
            return {
                ref,
                proposition: null,
                claimType: null,
                origin: null,
                inferenceMode: null,
                condition: null,
                superseded: false,
                missing: true,
                inputs: [],
            };
        }
        const cyclic = seen.has(input.id);
        return {
            ref,
            proposition: input.proposition,
            claimType: input.claimType,
            origin: input.origin,
            inferenceMode: input.inferenceMode,
            condition: input.condition,
            superseded: input.supersededAt !== null,
            missing: false,
            inputs: depth + 1 >= MAX_DEPTH || cyclic
                ? []
                : lineageNodes(input.derivedFrom, resolve, depth + 1, new Set([...seen, input.id])),
        };
    });
}
function anySuperseded(nodes) {
    return nodes.some((node) => node.superseded || anySuperseded(node.inputs));
}
function nearestCondition(nodes) {
    for (const node of nodes) {
        if (node.condition)
            return node.condition;
    }
    for (const node of nodes) {
        const found = nearestCondition(node.inputs);
        if (found)
            return found;
    }
    return null;
}
/** True when any claim input (transitively) has been superseded since. */
export function lineageHasSupersededInput(claim, resolve) {
    return anySuperseded(lineageNodes(claim.derivedFrom, resolve, 0, new Set([claim.id])));
}
/** Chronological history. The origin event is always first. */
export function claimHistory(claim) {
    const origin = {
        kind: "originated",
        at: claim.generatedAt ?? claim.createdAt,
        by: claim.createdBy,
        origin: claim.origin,
        claimType: claim.claimType,
        inferenceMode: claim.inferenceMode,
        condition: claim.condition,
        mechanism: claim.derivationMechanism,
        model: claim.derivationModel,
        conversationId: claim.derivationConversationId,
        messageId: claim.derivationMessageId,
    };
    const later = [
        ...claim.evidence.map((row) => ({
            kind: "evidence",
            at: row.recordedAt,
            relation: row.relation,
            authority: row.authority,
            source: row.source,
            asOf: row.asOf,
            sourceActorLabel: row.sourceActor?.label ?? null,
            recordedByKind: row.recordedByKind,
        })),
        ...claim.stances.map((row) => ({
            kind: "stance",
            at: row.recordedAt,
            actorLabel: row.actor.label,
            actorKind: row.actor.kind,
            stance: row.stance,
            source: row.source,
            asOf: row.asOf,
            recordedByKind: row.recordedByKind,
        })),
    ].sort((a, b) => a.at.localeCompare(b.at));
    if (claim.supersededAt) {
        later.push({
            kind: "superseded",
            at: claim.supersededAt,
            by: claim.supersededBy,
            reason: claim.supersededReason,
            supersededByClaimId: claim.supersededByClaimId,
        });
    }
    return [origin, ...later];
}
/**
 * Explain a claim from its structured lineage and history. Pure: the caller
 * supplies the claim and a resolver over the lineage closure it loaded.
 */
export function explainClaim(claim, resolve) {
    const assessment = assessClaim(claim, resolve);
    const lineage = lineageNodes(claim.derivedFrom, resolve, 0, new Set([claim.id]));
    return {
        claimId: claim.id,
        proposition: claim.proposition,
        claimType: claim.claimType,
        origin: claim.origin,
        reasoningMode: claim.inferenceMode,
        condition: claim.condition ?? nearestCondition(lineage),
        conditional: assessment.conditional,
        derived: assessment.derived,
        verification: assessment.verification,
        organisationStance: assessment.selfStance?.stance ?? null,
        agreed: assessment.agreed,
        superseded: claim.supersededAt !== null,
        lineageSuperseded: anySuperseded(lineage),
        lineage,
        history: claimHistory(claim),
    };
}
//# sourceMappingURL=epistemic-lineage.js.map